import React, { useEffect, useState } from "react";
import { Checkbox, Col, Form, Row, Table } from "antd";
import { useFetchConstant } from "apis/constant";
import {
  getLabelOfConstant,
  checkHasValue,
  constructDisplayPrice,
} from "utils/general";

import FormDatePicker from "components/FormDatePicker/FormDatePicker";
import FormInput from "components/FormInput/FormInput";
import SectionCard from "components/SectionCard/SectionCard";
import SectionContainer from "components/SectionContainer/SectionContainer";
import AddBenefitModal from "../AddBenefitModal/AddBenefitModal";

import {
  DescriptionForLabel,
  ExtraInfoText,
  FinancialValueText,
  Label,
} from "../PlanDetails/PlanDetails.styles";
import EditableTable from "./components/EditableTable/EditableTable";
import Locale from "react-intl-universal";

const { Item: FormItem } = Form;

const ExtraBenefits = ({
  defaultValues = {},
  planCoverages = [],
  updatePlanCoverage,
  updateSelectedType,
  uploadVisible,
  ...otherProps
}) => {
  const [
    isLifetimeLimitAmtUnlimited,
    setIsLifetimeLimitAmtUnlimited,
  ] = useState(false);
  const [lifetimeLimitAmt, setLifetimeLimitAmt] = useState("");

  // Plan coverage
  const {
    isLoading: isPlanTypesLoading,
    selection: planTypes,
  } = useFetchConstant("planTypes");
  const {
    isLoading: isInsuranceCompaniesLoading,
    selection: insuranceCompanies,
    data: insuranceCompaniesConst,
  } = useFetchConstant("insuranceCompanies");
  const {
    isLoading: isPolicyStatusesLoading,
    selection: policyStatuses,
  } = useFetchConstant("policyStatuses");
  const {
    isLoading: isBenefitTypesLoading,
    selection: benefitTypes,
  } = useFetchConstant("benefitTypes");
  const {
    isLoading: isPaymentModesLoading,
    selection: paymentModes,
  } = useFetchConstant("paymentModes");
  const {
    isLoading: isCoveragesTemplateLoading,
    selection: coveragesTemplate,
  } = useFetchConstant("coveragesTemplate");
  const isConstantsLoading =
    isPlanTypesLoading ||
    isInsuranceCompaniesLoading ||
    isPolicyStatusesLoading ||
    isBenefitTypesLoading ||
    isPaymentModesLoading;

  const [showPlanCoverageModal, setShowPlanCoverageModal] = useState(false);
  const [selectedPlanCoverageIndex, setSelectedPlanCoverageIndex] = useState(
    -1
  );
  const [
    shouldShowOtherCompanyInput,
    setShouldShowOtherCompanyInput,
  ] = useState(false);

  const planCoverageTypes = benefitTypes.filter(
    (benefitType) =>
      benefitType.type === "planCoverage" ||
      benefitType.code === "otherBenefit" ||
      benefitType.type === "waiver"
  );

  useEffect(() => {
    if (defaultValues.medicalCard) {
      const isLifetimeLimitAmtUnlimitedFormValue =
        defaultValues.medicalCard &&
        defaultValues.medicalCard.isLifetimeLimitAmtUnlimited;
      const lifetimeLimitAmtFormValue =
        defaultValues.medicalCard && defaultValues.medicalCard.lifetimeLimitAmt;
      setIsLifetimeLimitAmtUnlimited(isLifetimeLimitAmtUnlimitedFormValue);
      setLifetimeLimitAmt(lifetimeLimitAmtFormValue);
    }
  }, [defaultValues]);

  const handleOnPlanCoverageRowSelect = (index) => {
    setSelectedPlanCoverageIndex(index);
    setShowPlanCoverageModal(true);
  };

  const formatValues = (values) => ({
    ...values,
    amount: values.amount && Number(values.amount),
  });

  const handleOnAddPlanCoverageModalOkButtonClick = (values) => {
    if (selectedPlanCoverageIndex > -1) {
      let editedPlanCoverages = [...planCoverages];
      editedPlanCoverages[selectedPlanCoverageIndex] = formatValues(values);
      updatePlanCoverage(editedPlanCoverages);
      setSelectedPlanCoverageIndex(-1);
    } else {
      const newPlanCoverages = [...planCoverages, formatValues(values)];
      updatePlanCoverage(newPlanCoverages);
    }
    setShowPlanCoverageModal(false);
  };

  const handleOnAddPlanCoverageModalRemoveButtonClick = () => {
    const newPlanCoverages = [...planCoverages];
    newPlanCoverages.splice(selectedPlanCoverageIndex, 1);
    updatePlanCoverage(newPlanCoverages);
    setSelectedPlanCoverageIndex(-1);
    setShowPlanCoverageModal(false);
  };

  const handleOnAddPlanCoverageModalCancelButtonClick = () => {
    setSelectedPlanCoverageIndex(-1);
    setShowPlanCoverageModal(false);
  };

  const constructPlanCoveragesTableColumns = (benefitTypes) => [
    {
      dataIndex: "type",
      key: "type",
      render: (value, record) => (
        <div>
          <Label>
            {getLabelOfConstant(value, benefitTypes) || "-"}
            {record.label ? ` - ${record.label}` : ""}
          </Label>
          <DescriptionForLabel>{record.desc}</DescriptionForLabel>
        </div>
      ),
    },
    {
      dataIndex: "amount",
      key: "amount",
      render: (value) =>
        checkHasValue(value) ? (
          <FinancialValueText>
            {constructDisplayPrice(value, false)}
          </FinancialValueText>
        ) : (
          <FinancialValueText>Yes</FinancialValueText>
        ),
    },
    {
      dataIndex: "remarks",
      key: "remarks",
      render: (value) => <ExtraInfoText>{value}</ExtraInfoText>,
    },
  ];

  const PlanCoverageModal = showPlanCoverageModal && (
    <AddBenefitModal
      defaultValues={
        selectedPlanCoverageIndex > -1 &&
        planCoverages[selectedPlanCoverageIndex]
      }
      isLoading={isConstantsLoading}
      onOk={handleOnAddPlanCoverageModalOkButtonClick}
      onClose={handleOnAddPlanCoverageModalCancelButtonClick}
      onRemove={handleOnAddPlanCoverageModalRemoveButtonClick}
      title="Plan Coverage Details"
      types={planCoverageTypes}
      typeLabel="Coverage Type"
      visible={showPlanCoverageModal}
    />
  );

  return (
    <>
      <SectionContainer>
        <SectionCard
          loading={isConstantsLoading}
          title={Locale.get("ownedPolicies.coverageTab.sectionCardTitle_1")}
          // buttonText="Add Coverage"
          // onActionButtonClick={() => setShowPlanCoverageModal(true)}
        >
          <Row gutter={16}>
            {/* <Table
              dataSource={planCoverages}
              columns={constructPlanCoveragesTableColumns(benefitTypes)}
              pagination={false}
              rowKey={(record, index) => index}
              showHeader={false}
              style={{ width: "100%" }}
              onRow={(record, index) => {
                return {
                  onClick: () => handleOnPlanCoverageRowSelect(index),
                };
              }}
            /> */}
            <EditableTable
              uploadVisible={uploadVisible}
              data={planCoverages}
              isLoading={isBenefitTypesLoading}
              coverageTemplate={coveragesTemplate}
              updatePlanCoverage={updatePlanCoverage}
            />
          </Row>
        </SectionCard>
      </SectionContainer>
      <SectionContainer>
        <SectionCard title={Locale.get("ownedPolicies.coverageTab.medCard")}>
          <Row gutter={16}>
            <Col span={24} lg={12}>
              <FormInput
                label={Locale.get("ownedPolicies.coverageTab.name")}
                name={["medicalCard", "name"]}
                placeholder={Locale.get(
                  "ownedPolicies.coverageTab.medCardPlaceholder"
                )}
              />
            </Col>
            <Col span={24} lg={12}>
              <FormInput
                label={Locale.get("ownedPolicies.coverageTab.rab")}
                name={["medicalCard", "roomAndBoardAmt"]}
                placeholder="0.00"
                type="financial"
              />
            </Col>
            <Col span={24} lg={12}>
              <FormInput
                label={Locale.get("ownedPolicies.coverageTab.yearlyLimit")}
                name={["medicalCard", "yearlyLimitAmt"]}
                placeholder="0.00"
                type="financial"
              />
            </Col>
            <Col span={24} lg={12}>
              <FormInput
                disabled={!!isLifetimeLimitAmtUnlimited}
                label={Locale.get("ownedPolicies.coverageTab.lifetimeLimit")}
                name={["medicalCard", "lifetimeLimitAmt"]}
                placeholder="0.00"
                type="financial"
                // requiredErrorMessage={!isLifetimeLimitAmtUnlimited && !lifetimeLimitAmt && 'Please enter lifetime limit of this medical card.'}
                extraProps={{
                  onChange: (e) => setLifetimeLimitAmt(e.target.value),
                }}
              />
              <FormItem
                name={["medicalCard", "isLifetimeLimitAmtUnlimited"]}
                valuePropName="checked"
                style={{ marginTop: "-16px" }}
              >
                <Checkbox
                  disabled={!!lifetimeLimitAmt}
                  onClick={(e) => {
                    setIsLifetimeLimitAmtUnlimited(e.target.checked);
                  }}
                >
                  {Locale.get("ownedPolicies.coverageTab.unlimitedLifetime")}
                </Checkbox>
              </FormItem>
            </Col>
            <Col span={24} lg={12}>
              <FormInput
                label={Locale.get(
                  "ownedPolicies.coverageTab.outpatientCancerTreat"
                )}
                name={["medicalCard", "outpatientCancerTreatmentAmt"]}
                placeholder="0.00"
                type="financial"
              />
            </Col>
            <Col span={24} lg={12}>
              <FormInput
                label={Locale.get(
                  "ownedPolicies.coverageTab.outpatientKidneyDialysisTreat"
                )}
                name={["medicalCard", "outpatientKidneyDialysisTreatmentAmt"]}
                placeholder="0.00"
                type="financial"
              />
            </Col>

            <Col span={24} lg={12}>
              <FormInput
                label={Locale.get("ownedPolicies.coverageTab.deductible")}
                name={["medicalCard", "deductible"]}
                placeholder="0.00"
                type="financial"
              />
            </Col>

            <Col span={24} lg={12}>
              <FormInput
                label={Locale.get("ownedPolicies.coverageTab.coInsurance")}
                name={["medicalCard", "coInsurance"]}
                placeholder={Locale.get(
                  "ownedPolicies.coverageTab.coInsurancePlaceholder"
                )}
                // type="financial"
              />
            </Col>

            <Col span={24} lg={12}>
              <FormInput
                label={Locale.get("ownedPolicies.coverageTab.remarks")}
                name={["medicalCard", "remarks"]}
                placeholder={Locale.get(
                  "ownedPolicies.coverageTab.remarkPlaceholder"
                )}
                // type="financial"
                type="textArea"
              />
            </Col>
          </Row>
        </SectionCard>
      </SectionContainer>
      <SectionContainer>
        <SectionCard
          title={Locale.get("ownedPolicies.coverageTab.hospitalizationBenefit")}
        >
          <Row gutter={16}>
            <Col span={24} lg={12}>
              <FormInput
                label={Locale.get("ownedPolicies.coverageTab.hospitalSurgical")}
                name={[
                  "hospitalizationBenefits",
                  "hospitalAndSurgicalBenefit",
                  "amount",
                ]}
                placeholder="0.00"
                type="financial"
              />
            </Col>
            <Col span={24} lg={12}>
              <FormInput
                label={Locale.get(
                  "ownedPolicies.coverageTab.postHospitalization"
                )}
                name={[
                  "hospitalizationBenefits",
                  "postHospitalization",
                  "amount",
                ]}
                placeholder="0.00"
                type="financial"
              />
            </Col>
            <Col span={24} lg={12}>
              <FormInput
                label={Locale.get("ownedPolicies.coverageTab.dailyBenefit")}
                name={["hospitalizationBenefits", "dailyBenefit", "amount"]}
                placeholder="0.00"
                type="financial"
              />
            </Col>
          </Row>
        </SectionCard>
      </SectionContainer>
      <SectionContainer>
        <SectionCard title={Locale.get("ownedPolicies.coverageTab.femaleOnly")}>
          <Row gutter={16}>
            <Col span={24} lg={12}>
              <FormInput
                label={Locale.get("ownedPolicies.coverageTab.cis")}
                name={["femaleBenefits", "carsinomaInSituBenefit", "amount"]}
                placeholder="0.00"
                type="financial"
              />
            </Col>
            <Col span={24} lg={12}>
              <FormInput
                label={Locale.get(
                  "ownedPolicies.coverageTab.femaleCancerBenefit"
                )}
                name={["femaleBenefits", "cancerBenefit", "amount"]}
                placeholder="0.00"
                type="financial"
              />
            </Col>
            <Col span={24} lg={12}>
              <FormInput
                label={Locale.get("ownedPolicies.coverageTab.fdb")}
                name={[
                  "femaleBenefits",
                  "facialDisfigurementBenefit",
                  "amount",
                ]}
                placeholder="0.00"
                type="financial"
              />
            </Col>
            <Col span={24} lg={12}>
              <FormInput
                label={Locale.get(
                  "ownedPolicies.coverageTab.yearlyMedCheckupFee"
                )}
                name={["femaleBenefits", "medicalCheckupBenefit", "amount"]}
                placeholder="0.00"
                type="financial"
              />
            </Col>
            <Col span={24} lg={12}>
              <FormInput
                label={Locale.get("ownedPolicies.coverageTab.retirementFund")}
                name={["femaleBenefits", "retirementFundBenefit", "amount"]}
                placeholder="0.00"
                type="financial"
              />
            </Col>
            <Col span={24} lg={12}>
              <FormInput
                label={Locale.get("ownedPolicies.coverageTab.pcb")}
                name={[
                  "femaleBenefits",
                  "pregnancyComplicationBenefit",
                  "amount",
                ]}
                placeholder="0.00"
                type="financial"
              />
            </Col>
            <Col span={24} lg={12}>
              <FormInput
                label={Locale.get("ownedPolicies.coverageTab.dbca")}
                name={[
                  "femaleBenefits",
                  "congenitalAnomaliesDeliveryBenefit",
                  "amount",
                ]}
                placeholder="0.00"
                type="financial"
              />
            </Col>
            <Col span={24} lg={12}>
              <FormInput
                label={Locale.get(
                  "ownedPolicies.coverageTab.dHospitalizationBenefit"
                )}
                name={[
                  "femaleBenefits",
                  "dailyHospitalizationBenefit",
                  "amount",
                ]}
                placeholder="0.00"
                type="financial"
              />
            </Col>
            <Col span={24} lg={12}>
              <FormInput
                label={Locale.get("ownedPolicies.coverageTab.dDueToChildBirth")}
                name={["femaleBenefits", "childbirthDeathBenefit", "amount"]}
                placeholder="0.00"
                type="financial"
              />
            </Col>
          </Row>
        </SectionCard>
      </SectionContainer>
      <SectionContainer>
        <SectionCard
          title={Locale.get("ownedPolicies.coverageTab.cashGuaranteePayment")}
        >
          <Row gutter={16}>
            <Col span={24} lg={12}>
              <FormDatePicker
                label={Locale.get("ownedPolicies.coverageTab.startDate")}
                name={["yearlyGuaranteedCashPayment", "startingDate"]}
                placeholder="2020-01-01"
              />
            </Col>
            <Col span={24} lg={12}>
              {/* <FormInput
                label="Amount"
                name={["yearlyGuaranteedCashPayment", "amount"]}
                placeholder="0.00"
                type="financial"
              /> */}
              <FormDatePicker
                label={Locale.get("ownedPolicies.coverageTab.maturityDate")}
                name={["yearlyGuaranteedCashPayment", "maturityDate"]}
                placeholder="2020-01-01"
              />
            </Col>
            <Col span={24} lg={12}>
              {/* <FormInput
                label="Funds Estimate at 20 years"
                name={["yearlyGuaranteedCashPayment", "fundsAt20YearsAmt"]}
                placeholder="0.00"
                type="financial"
              /> */}
              <FormInput
                label={Locale.get("ownedPolicies.coverageTab.paymentCoupon")}
                name={[
                  "yearlyGuaranteedCashPayment",
                  "paymentOrCouponIntervals",
                ]}
                placeholder={Locale.get(
                  "ownedPolicies.coverageTab.paymentCouponPlaceholder"
                )}
              />
            </Col>
            <Col span={24} lg={12}>
              <FormInput
                label={Locale.get("ownedPolicies.coverageTab.amount")}
                name={["yearlyGuaranteedCashPayment", "amount"]}
                placeholder="0.00"
                type="financial"
              />
            </Col>
            <Col span={24} lg={12}>
              <FormInput
                label={Locale.get("ownedPolicies.coverageTab.estFundMaturity")}
                name={[
                  "yearlyGuaranteedCashPayment",
                  "fundsMaturity",
                  "estimateFunds",
                ]}
                placeholder="0.00"
                type="financial"
              />
            </Col>
            <Col span={24} lg={12}>
              <FormInput
                label={Locale.get("ownedPolicies.coverageTab.whenMaturity")}
                name={["yearlyGuaranteedCashPayment", "fundsMaturity", "age"]}
                placeholder={Locale.get(
                  "ownedPolicies.coverageTab.whenMaturityPlaceholder"
                )}
              />
            </Col>
            <Col span={24} lg={24}>
              <FormInput
                label={Locale.get("ownedPolicies.coverageTab.note")}
                name={["yearlyGuaranteedCashPayment", "note"]}
                placeholder={Locale.get(
                  "ownedPolicies.coverageTab.notePlaceholder"
                )}
                type="textArea"
              />
            </Col>
          </Row>
        </SectionCard>
      </SectionContainer>
      {PlanCoverageModal}
    </>
  );
};

export default ExtraBenefits;
